import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  FacebookLogo, InstagramLogo, WhatsappLogo, GoogleLogo, Globe, Envelope,
  Users, TrendUp, ChatCircleText, IdentificationCard, Gear, Plug, MagnifyingGlass,
  UserCircle, Phone, PaperPlaneTilt, PlusCircle, CheckCircle, ArrowRight, Sparkle, House,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const SOURCE_META = {
  facebook: { icon: FacebookLogo, color: "text-blue-600", bg: "bg-blue-50", ring: "border-blue-200", label: "Facebook" },
  instagram: { icon: InstagramLogo, color: "text-pink-600", bg: "bg-pink-50", ring: "border-pink-200", label: "Instagram" },
  whatsapp: { icon: WhatsappLogo, color: "text-emerald-600", bg: "bg-emerald-50", ring: "border-emerald-200", label: "WhatsApp" },
  google: { icon: GoogleLogo, color: "text-amber-600", bg: "bg-amber-50", ring: "border-amber-200", label: "Google" },
  website: { icon: Globe, color: "text-indigo-600", bg: "bg-indigo-50", ring: "border-indigo-200", label: "Website" },
  email: { icon: Envelope, color: "text-slate-600", bg: "bg-slate-50", ring: "border-slate-200", label: "Email" },
  walk_in: { icon: UserCircle, color: "text-slate-600", bg: "bg-slate-50", ring: "border-slate-200", label: "Walk-in" },
  referral: { icon: Users, color: "text-purple-600", bg: "bg-purple-50", ring: "border-purple-200", label: "Referral" },
  manual: { icon: UserCircle, color: "text-slate-600", bg: "bg-slate-50", ring: "border-slate-200", label: "Manual" },
};

const STATUS_META = {
  New: "bg-blue-100 text-blue-800 border-blue-200",
  "Follow-up": "bg-amber-100 text-amber-800 border-amber-200",
  Contacted: "bg-indigo-100 text-indigo-800 border-indigo-200",
  Converted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Lost: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function Marketing() {
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [filterSource, setFilterSource] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [activeLead, setActiveLead] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [newForm, setNewForm] = useState({ name: "", mobile: "", email: "", message: "", source: "manual", campaign: "", priority: "Normal" });
  const [connectForm, setConnectForm] = useState({});
  const navigate = useNavigate();

  const loadStats = useCallback(() => api.get("/leads/stats").then(r => setStats(r.data)).catch(() => {}), []);
  const loadLeads = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (filterSource !== "all") params.source = filterSource;
    if (filterStatus !== "all") params.status = filterStatus;
    return api.get("/leads", { params }).then(r => setLeads(r.data)).catch(() => {});
  }, [q, filterSource, filterStatus]);
  const loadIntegrations = useCallback(() => api.get("/marketing/integrations").then(r => setIntegrations(r.data)).catch(() => {}), []);

  useEffect(() => { loadStats(); loadLeads(); loadIntegrations(); }, [loadStats, loadLeads, loadIntegrations]);

  const createLead = async () => {
    if (!newForm.name.trim() || !newForm.mobile.trim()) { toast.error("Name and mobile required"); return; }
    try {
      await api.post("/leads", newForm);
      toast.success("Lead created");
      setNewOpen(false);
      setNewForm({ name: "", mobile: "", email: "", message: "", source: "manual", campaign: "", priority: "Normal" });
      loadStats(); loadLeads();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const updateStatus = async (lead, status) => {
    try {
      const { data } = await api.patch(`/leads/${lead.id}`, { status });
      toast.success(`Marked ${status}`);
      setActiveLead(data);
      loadStats(); loadLeads();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const convertLead = async (lead, alsoCreateApp = false) => {
    try {
      const { data } = await api.post(`/leads/${lead.id}/convert`, {
        also_create_application: alsoCreateApp,
      });
      toast.success(alsoCreateApp ? "Customer + Application created" : "Customer created");
      if (alsoCreateApp && data.application_id) navigate(`/applications`);
      else if (data.customer_id) navigate(`/customers?id=${data.customer_id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const analyzeLead = async (lead) => {
    try {
      const { data } = await api.post(`/leads/${lead.id}/analyze`);
      toast.success(data.detected_service ? `Detected: ${data.detected_service}` : "No specific service detected");
      setActiveLead({ ...lead, detected_service: data.detected_service, suggested_docs: data.suggested_docs });
      loadLeads();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const saveConnections = async () => {
    try {
      const { data } = await api.put("/marketing/integrations", connectForm);
      setIntegrations(data);
      setConnectOpen(false);
      setConnectForm({});
      toast.success("Marketing integrations saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const openConnect = () => {
    setConnectForm({
      facebook_page_id: integrations?.facebook_page_id || "",
      facebook_page_name: integrations?.facebook_page_name || "",
      instagram_business_id: integrations?.instagram_business_id || "",
      instagram_username: integrations?.instagram_username || "",
      whatsapp_phone_id: integrations?.whatsapp_phone_id || "",
      whatsapp_display_number: integrations?.whatsapp_display_number || "",
      website_domain: integrations?.website_domain || "",
      google_business_id: integrations?.google_business_id || "",
      meta_verify_token: "",
      meta_access_token: "",
      meta_app_secret: "",
    });
    setConnectOpen(true);
  };

  const bySrc = stats?.by_source || {};
  const filteredLeads = useMemo(() => leads, [leads]);

  return (
    <div className="p-6 lg:p-8 max-w-[1600px]">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-indigo-600 font-semibold mb-2 flex items-center gap-2">
            <TrendUp size={14} weight="fill" /> Marketing & Lead CRM
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Marketing Dashboard</h1>
          <p className="text-sm text-slate-600 mt-1.5">Facebook, Instagram, WhatsApp, Google & Website leads — captured, AI-tagged, converted to customers.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openConnect} data-testid="mkt-connect-btn" className="gap-2">
            <Plug size={16} weight="bold" /> Connect Accounts
          </Button>
          <Button onClick={() => setNewOpen(true)} data-testid="mkt-new-lead-btn" className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white">
            <PlusCircle size={16} weight="bold" /> New Lead
          </Button>
        </div>
      </div>

      {/* Connection Status Row */}
      {integrations && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-6" data-testid="mkt-connections">
          <ConnectionPill icon={FacebookLogo} label="Facebook" name={integrations.facebook_page_name || integrations.facebook_page_id} connected={integrations.facebook_connected} color="text-blue-600" />
          <ConnectionPill icon={InstagramLogo} label="Instagram" name={integrations.instagram_username || integrations.instagram_business_id} connected={integrations.instagram_connected} color="text-pink-600" />
          <ConnectionPill icon={WhatsappLogo} label="WhatsApp" name={integrations.whatsapp_display_number} connected={integrations.whatsapp_connected} color="text-emerald-600" />
          <ConnectionPill icon={Globe} label="Website" name={integrations.website_domain} connected={integrations.website_connected} color="text-indigo-600" />
          <ConnectionPill icon={GoogleLogo} label="Google Business" name={integrations.google_business_id} connected={integrations.google_connected} color="text-amber-600" />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="dashboard" data-testid="mkt-tab-dashboard">Overview</TabsTrigger>
          <TabsTrigger value="leads" data-testid="mkt-tab-leads">Lead Inbox {stats && <Badge variant="outline" className="ml-2 text-xs">{stats.total}</Badge>}</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="dashboard" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Total Leads" value={stats?.total ?? 0} accent="indigo" icon={Users} />
            <StatCard label="Today" value={stats?.today ?? 0} accent="emerald" icon={TrendUp} />
            <StatCard label="Converted" value={stats?.converted ?? 0} accent="orange" icon={CheckCircle} />
            <StatCard label="Conversion Rate" value={`${stats?.conversion_rate ?? 0}%`} accent="rose" icon={TrendUp} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Leads by source */}
            <Card className="p-4 bg-white">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Leads by Source</div>
              <div className="space-y-2.5">
                {Object.entries(bySrc).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).map(([src, count]) => {
                  const meta = SOURCE_META[src] || SOURCE_META.manual;
                  const Icon = meta.icon;
                  const pct = stats.total ? Math.round((count / stats.total) * 100) : 0;
                  return (
                    <div key={src} className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-md ${meta.bg} border ${meta.ring} flex items-center justify-center shrink-0`}>
                        <Icon size={16} weight="fill" className={meta.color} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium text-slate-800">{meta.label}</span>
                          <span className="text-slate-500">{count} · {pct}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-slate-100 mt-1 overflow-hidden">
                          <div className={`h-full ${meta.color.replace("text-", "bg-")}`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
                {Object.values(bySrc).every(v => v === 0) && <div className="text-sm text-slate-400 py-6 text-center">No leads yet.</div>}
              </div>
            </Card>

            {/* Pipeline */}
            <Card className="p-4 bg-white">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Pipeline</div>
              <div className="space-y-2">
                {Object.entries(stats?.by_status || {}).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between p-2 rounded border border-slate-100 hover:bg-slate-50 cursor-pointer"
                       onClick={() => { setFilterStatus(status); setTab("leads"); }}>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${STATUS_META[status] || STATUS_META.New}`}>{status}</span>
                    <span className="font-bold text-slate-800">{count}</span>
                  </div>
                ))}
                {(stats?.pending_followups ?? 0) > 0 && (
                  <div className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 flex items-center gap-2">
                    <span className="text-amber-600">⏳</span>
                    <span><b>{stats.pending_followups}</b> lead(s) unanswered for 6+ hours — nudge required.</span>
                  </div>
                )}
              </div>
            </Card>
          </div>

          <Card className="p-4 bg-white">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Recent Leads</div>
              <button onClick={() => setTab("leads")} className="text-xs text-indigo-600 hover:underline">View all →</button>
            </div>
            <LeadTable leads={leads.slice(0, 8)} onOpen={setActiveLead} compact />
          </Card>
        </TabsContent>

        {/* Lead Inbox */}
        <TabsContent value="leads">
          <Card className="p-4 bg-white space-y-3">
            <div className="flex flex-wrap gap-2 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="mkt-lead-search" placeholder="Search lead no, name, mobile, message…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9 text-sm" />
              </div>
              <Select value={filterSource} onValueChange={setFilterSource}>
                <SelectTrigger className="h-9 w-[140px] text-sm" data-testid="mkt-filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sources</SelectItem>
                  {Object.entries(SOURCE_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="h-9 w-[140px] text-sm" data-testid="mkt-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {["New", "Follow-up", "Contacted", "Converted", "Lost"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <LeadTable leads={filteredLeads} onOpen={setActiveLead} />
          </Card>
        </TabsContent>
      </Tabs>

      {/* Lead detail drawer */}
      <Sheet open={!!activeLead} onOpenChange={(v) => !v && setActiveLead(null)}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-white">
          {activeLead && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <span className="font-mono text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">{activeLead.lead_no}</span>
                  <span>{activeLead.name}</span>
                </SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <SourceBadge source={activeLead.source} />
                  <span className={`px-2 py-0.5 rounded border font-semibold ${STATUS_META[activeLead.status] || STATUS_META.New}`}>{activeLead.status}</span>
                  {activeLead.priority && activeLead.priority !== "Normal" && (
                    <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 font-semibold">{activeLead.priority}</span>
                  )}
                </div>

                {activeLead.customer_id && (
                  <div className="p-2.5 rounded bg-emerald-50 border border-emerald-200 text-xs text-emerald-800 flex items-center gap-2">
                    <CheckCircle size={14} weight="fill" />
                    <span>Linked to existing customer <b className="font-mono">{activeLead.customer_no}</b></span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Info label="Mobile" value={activeLead.mobile} mono />
                  <Info label="Email" value={activeLead.email} />
                  <Info label="Campaign" value={activeLead.campaign} />
                  <Info label="Assigned to" value={activeLead.assigned_to} />
                  {activeLead.landing_page && <Info label="Landing page" value={activeLead.landing_page} />}
                  {activeLead.utm_source && <Info label="UTM Source" value={activeLead.utm_source} />}
                </div>

                {activeLead.message && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Message</div>
                    <div className="text-sm text-slate-700 bg-slate-50 p-3 rounded border border-slate-200 whitespace-pre-wrap">{activeLead.message}</div>
                  </div>
                )}

                <div className="p-3 rounded bg-indigo-50 border border-indigo-100">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-indigo-600 font-semibold flex items-center gap-1.5 mb-2">
                    <Sparkle size={12} weight="fill" /> AI Detection
                  </div>
                  <div className="flex items-center gap-2 flex-wrap text-xs mb-2">
                    <span className="text-slate-500">Service:</span>
                    <span className="font-semibold text-indigo-800 bg-white px-2 py-0.5 rounded border border-indigo-200" data-testid="mkt-detected-service">
                      {activeLead.detected_service || "— not detected —"}
                    </span>
                    <Button size="sm" variant="outline" onClick={() => analyzeLead(activeLead)} className="h-6 text-[10px] px-2" data-testid="mkt-analyze-btn">Re-analyze</Button>
                  </div>
                  {activeLead.suggested_docs?.length > 0 && (
                    <ul className="list-disc list-inside text-xs text-slate-700 space-y-0.5">
                      {activeLead.suggested_docs.slice(0, 6).map((d, i) => <li key={i}>{d}</li>)}
                    </ul>
                  )}
                </div>

                {/* Status quick actions */}
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Status</div>
                  <div className="flex gap-1.5 flex-wrap">
                    {["New", "Follow-up", "Contacted", "Converted", "Lost"].map((s) => (
                      <button key={s} onClick={() => updateStatus(activeLead, s)} data-testid={`mkt-status-${s}`}
                              className={`text-[11px] px-2 py-1 rounded border transition-colors ${activeLead.status === s ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Convert actions */}
                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
                  <Button size="sm" variant="outline" onClick={() => convertLead(activeLead, false)} data-testid="mkt-convert-cust" className="gap-1 col-span-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                    <IdentificationCard size={14} weight="fill" /> Convert to Customer
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => convertLead(activeLead, true)} data-testid="mkt-convert-app" className="gap-1">
                    <House size={14} weight="fill" /> + Application
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(`tel:${activeLead.mobile}`)} data-testid="mkt-call-btn" className="gap-1">
                    <Phone size={14} weight="fill" /> Call
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(`https://wa.me/${activeLead.mobile.replace(/\D/g, "")}`, "_blank")} data-testid="mkt-wa-btn" className="gap-1 col-span-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                    <WhatsappLogo size={14} weight="fill" /> Open in WhatsApp
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* New Lead Dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle>New Lead</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Field label="Name *"><Input data-testid="mkt-new-name" value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })} /></Field>
              <Field label="Mobile *"><Input data-testid="mkt-new-mobile" value={newForm.mobile} onChange={(e) => setNewForm({ ...newForm, mobile: e.target.value })} /></Field>
              <Field label="Email"><Input value={newForm.email} onChange={(e) => setNewForm({ ...newForm, email: e.target.value })} /></Field>
              <Field label="Source">
                <Select value={newForm.source} onValueChange={(v) => setNewForm({ ...newForm, source: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(SOURCE_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Campaign"><Input value={newForm.campaign} onChange={(e) => setNewForm({ ...newForm, campaign: e.target.value })} /></Field>
              <Field label="Priority">
                <Select value={newForm.priority} onValueChange={(v) => setNewForm({ ...newForm, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["Normal", "Urgent", "Emergency"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Message / enquiry"><Textarea rows={3} value={newForm.message} onChange={(e) => setNewForm({ ...newForm, message: e.target.value })} /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>Cancel</Button>
            <Button onClick={createLead} data-testid="mkt-new-save-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white">Create Lead</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Connect Accounts Dialog */}
      <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
        <DialogContent className="max-w-2xl bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Plug size={18} weight="fill" /> Connect Marketing Accounts</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded p-2.5">
            <b>Official integrations only.</b> Meta requires you to register your Facebook Page, Instagram Business account, and WhatsApp Business number via <a href="https://business.facebook.com" target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">business.facebook.com</a>. Paste the credentials below — the ERP will store them encrypted.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="col-span-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-2">Facebook Page</div>
            <Field label="Page ID"><Input data-testid="mkt-cx-fb-page-id" value={connectForm.facebook_page_id || ""} onChange={(e) => setConnectForm({ ...connectForm, facebook_page_id: e.target.value })} placeholder="e.g. 10228812934…" /></Field>
            <Field label="Page name"><Input value={connectForm.facebook_page_name || ""} onChange={(e) => setConnectForm({ ...connectForm, facebook_page_name: e.target.value })} placeholder="Priyanka Enterprise" /></Field>

            <div className="col-span-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-2">Instagram Business</div>
            <Field label="IG Business ID"><Input value={connectForm.instagram_business_id || ""} onChange={(e) => setConnectForm({ ...connectForm, instagram_business_id: e.target.value })} /></Field>
            <Field label="Username"><Input value={connectForm.instagram_username || ""} onChange={(e) => setConnectForm({ ...connectForm, instagram_username: e.target.value })} placeholder="@priyanka_enterprise" /></Field>

            <div className="col-span-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-2">WhatsApp Business Cloud API</div>
            <Field label="Phone Number ID"><Input value={connectForm.whatsapp_phone_id || ""} onChange={(e) => setConnectForm({ ...connectForm, whatsapp_phone_id: e.target.value })} /></Field>
            <Field label="Display Number"><Input value={connectForm.whatsapp_display_number || ""} onChange={(e) => setConnectForm({ ...connectForm, whatsapp_display_number: e.target.value })} placeholder="+91 98xxxxxxxx" /></Field>

            <div className="col-span-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-2">Website</div>
            <Field label="Domain" full><Input value={connectForm.website_domain || ""} onChange={(e) => setConnectForm({ ...connectForm, website_domain: e.target.value })} placeholder="priyanka-enterprise.in" /></Field>

            <div className="col-span-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-2">Google Business Profile</div>
            <Field label="Business ID" full><Input value={connectForm.google_business_id || ""} onChange={(e) => setConnectForm({ ...connectForm, google_business_id: e.target.value })} /></Field>

            <div className="col-span-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-2">Meta App Credentials (stored encrypted)</div>
            <Field label="Access Token"><Input type="password" value={connectForm.meta_access_token || ""} onChange={(e) => setConnectForm({ ...connectForm, meta_access_token: e.target.value })} placeholder="EAAG…" /></Field>
            <Field label="App Secret"><Input type="password" value={connectForm.meta_app_secret || ""} onChange={(e) => setConnectForm({ ...connectForm, meta_app_secret: e.target.value })} placeholder="a1b2c3…" /></Field>
            <Field label="Webhook Verify Token" full><Input value={connectForm.meta_verify_token || ""} onChange={(e) => setConnectForm({ ...connectForm, meta_verify_token: e.target.value })} placeholder="pick any random string; paste same into Meta App console" /></Field>
          </div>
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2.5">
            <b>Webhook URL to paste in Meta App Console:</b><br/>
            <code className="text-[11px] font-mono">{window.location.origin}/api/webhooks/meta-leadads</code>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectOpen(false)}>Cancel</Button>
            <Button onClick={saveConnections} data-testid="mkt-connect-save-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white">Save & Connect</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LeadTable({ leads, onOpen, compact = false }) {
  if (!leads.length) return <div className="text-center text-sm text-slate-400 py-8">No leads yet.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-200">
          <tr>
            <th className="text-left py-2 pr-3">Lead #</th>
            <th className="text-left py-2 pr-3">Name / Mobile</th>
            <th className="text-left py-2 pr-3">Source</th>
            <th className="text-left py-2 pr-3">Service</th>
            <th className="text-left py-2 pr-3">Campaign</th>
            {!compact && <th className="text-left py-2 pr-3">Assigned</th>}
            <th className="text-left py-2 pr-3">Status</th>
            <th className="text-right py-2 pr-1">Action</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((l) => (
            <tr key={l.id} data-testid={`mkt-lead-row-${l.lead_no}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
              <td className="py-2 pr-3 font-mono text-xs text-indigo-700">{l.lead_no}</td>
              <td className="py-2 pr-3">
                <div className="font-medium text-slate-800">{l.name}</div>
                <div className="text-xs text-slate-500 font-mono">{l.mobile}</div>
              </td>
              <td className="py-2 pr-3"><SourceBadge source={l.source} /></td>
              <td className="py-2 pr-3 text-slate-700">{l.detected_service || l.service_hint || <span className="text-slate-400">—</span>}</td>
              <td className="py-2 pr-3 text-slate-600 text-xs">{l.campaign || <span className="text-slate-400">—</span>}</td>
              {!compact && <td className="py-2 pr-3 text-slate-600 text-xs">{l.assigned_to || <span className="text-slate-400">—</span>}</td>}
              <td className="py-2 pr-3"><span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${STATUS_META[l.status] || STATUS_META.New}`}>{l.status}</span></td>
              <td className="py-2 pr-1 text-right">
                <Button size="sm" variant="outline" onClick={() => onOpen(l)} data-testid={`mkt-open-${l.lead_no}`} className="h-7 text-xs gap-1">
                  Open <ArrowRight size={11} weight="bold" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SourceBadge({ source }) {
  const meta = SOURCE_META[source] || SOURCE_META.manual;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded border ${meta.bg} ${meta.ring}`}>
      <Icon size={11} weight="fill" className={meta.color} /> <span className="text-slate-700">{meta.label}</span>
    </span>
  );
}

function ConnectionPill({ icon: Icon, label, name, connected, color }) {
  return (
    <div className={`p-2.5 rounded-md border flex items-center gap-2 ${connected ? "bg-white border-emerald-300" : "bg-slate-50 border-slate-200"}`}>
      <Icon size={20} weight="fill" className={color} />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className="text-xs truncate">
          {connected ? (
            <span className="text-emerald-700 font-medium flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block"></span>{name || "Connected"}</span>
          ) : (
            <span className="text-slate-400">Not connected</span>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent, icon: Icon }) {
  const border = { indigo: "border-l-indigo-500", emerald: "border-l-emerald-500", orange: "border-l-orange-500", rose: "border-l-rose-500" };
  return (
    <Card className={`p-3 border-l-4 ${border[accent] || border.indigo} bg-white`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
          <div className="text-2xl font-bold text-slate-900 mt-0.5" style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
        </div>
        {Icon && <Icon size={20} weight="fill" className="text-slate-300" />}
      </div>
    </Card>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">{label}</div>
      {children}
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`text-sm text-slate-700 ${mono ? "font-mono" : ""}`}>{value || <span className="text-slate-300">—</span>}</div>
    </div>
  );
}
